'use client'

import { useState } from 'react'
import {
  Users,
  BarChart2,
  Briefcase,
  Package,
  DollarSign,
  LayoutDashboard,
  Headphones,
  ShieldCheck,
  ArrowRight,
} from 'lucide-react'
import { useT } from '@/lib/useT'

const featureConfig = [
  { titleKey: 'features.salesTitle', descKey: 'features.salesDesc', tagKey: 'features.sales', icon: Users, accent: '#1A535A', accentBg: 'rgba(26,83,90,0.08)' },
  { titleKey: 'features.peopleTitle', descKey: 'features.peopleDesc', tagKey: 'features.people', icon: Briefcase, accent: '#22614A', accentBg: 'rgba(34,97,74,0.08)' },
  { titleKey: 'features.financeTitle', descKey: 'features.financeDesc', tagKey: 'features.finance', icon: DollarSign, accent: '#9C5800', accentBg: 'rgba(156,88,0,0.08)' },
  { titleKey: 'features.projectsTitle', descKey: 'features.projectsDesc', tagKey: 'features.projects', icon: LayoutDashboard, accent: '#22505A', accentBg: 'rgba(34,80,90,0.08)' },
  { titleKey: 'features.supplyChainTitle', descKey: 'features.supplyChainDesc', tagKey: 'features.supplyChain', icon: Package, accent: '#5C554E', accentBg: 'rgba(92,85,78,0.08)' },
  { titleKey: 'features.intelligenceTitle', descKey: 'features.intelligenceDesc', tagKey: 'features.intelligence', icon: BarChart2, accent: '#003F47', accentBg: 'rgba(0,63,71,0.08)' },
  { titleKey: 'features.supportTitle', descKey: 'features.supportDesc', tagKey: 'features.support', icon: Headphones, accent: '#952E27', accentBg: 'rgba(149,46,39,0.08)' },
  { titleKey: 'features.adminTitle', descKey: 'features.adminDesc', tagKey: 'features.admin', icon: ShieldCheck, accent: '#3E3830', accentBg: 'rgba(62,56,48,0.08)' },
]

function FeatureCard({ feature, t }: { feature: (typeof featureConfig)[number], t: (key: string) => string }) {
  const [hovered, setHovered] = useState(false)
  const Icon = feature.icon

  return (
    <li
      className="group relative flex flex-col bg-white rounded-[18px] border border-[#E2E4E5] overflow-hidden cursor-default"
      style={{
        boxShadow: hovered
          ? '0 4px 8px rgba(10,23,29,0.06), 0 18px 40px rgba(10,23,29,0.12)'
          : '0 1px 2px rgba(10,23,29,0.06), 0 2px 6px rgba(10,23,29,0.06)',
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
        transition: 'box-shadow 220ms cubic-bezier(0.4,0,0.2,1), transform 220ms cubic-bezier(0.4,0,0.2,1)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Top accent bar */}
      <div
        className="h-0.5 w-full transition-all duration-[220ms]"
        style={{
          background: hovered
            ? `linear-gradient(90deg, ${feature.accent}, transparent)`
            : 'transparent',
        }}
        aria-hidden="true"
      />

      <div className="flex flex-col gap-5 p-6 flex-1">
        {/* Icon + tag row */}
        <div className="flex items-start justify-between">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-[12px] transition-colors duration-[220ms]"
            style={{
              backgroundColor: hovered ? `${feature.accent}22` : feature.accentBg,
            }}
            aria-hidden="true"
          >
            <Icon size={22} style={{ color: feature.accent }} strokeWidth={1.75} />
          </div>
          <span
            className="text-[11px] font-semibold uppercase tracking-[0.1em] px-2.5 py-1 rounded-[999px]"
            style={{
              color: feature.accent,
              backgroundColor: feature.accentBg,
            }}
          >
            {t(feature.tagKey as any)}
          </span>
        </div>

        {/* Text */}
        <div className="flex flex-col gap-2">
          <h3 className="text-[1rem] font-semibold text-[#0A171D] leading-snug tracking-[-0.01em]">
            {t(feature.titleKey as any)}
          </h3>
          <p className="text-sm text-[#6C7377] leading-relaxed">
            {t(feature.descKey as any)}
          </p>
        </div>

        {/* Learn more link */}
        <div
          className="flex items-center gap-1.5 transition-all duration-[220ms] mt-auto"
          style={{
            opacity: hovered ? 1 : 0,
            transform: hovered ? 'translateY(0)' : 'translateY(4px)',
          }}
          aria-hidden={!hovered}
        >
          <span className="text-sm font-semibold" style={{ color: feature.accent }}>
            Learn more
          </span>
          <ArrowRight size={14} style={{ color: feature.accent }} strokeWidth={2} />
        </div>
      </div>
    </li>
  )
}

export default function FeaturesGrid() {
  const t = useT()
  
  return (
    <section
      id="features"
      className="py-24 bg-[#FFF9F2]"
      aria-labelledby="features-heading"
    >
      <div className="mx-auto max-w-[1200px] px-6">
        {/* Section header */}
        <div className="flex flex-col items-center text-center gap-4 mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#FFBD76]">
            {t('features.platformModules')}
          </p>
          <h2
            id="features-heading"
            className="text-3xl md:text-4xl font-bold text-[#0A171D] tracking-[-0.02em] leading-[1.08] text-balance"
          >
            {t('features.heading')}
          </h2>
          <p className="max-w-xl text-base md:text-lg text-[#6C7377] leading-relaxed">
            {t('features.subheading')}
          </p>
        </div>

        {/* Grid */}
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
          role="list"
        >
          {featureConfig.map((f) => (
            <FeatureCard key={f.titleKey} feature={f} t={t} />
          ))}
        </ul>
      </div>
    </section>
  )
}
